/**
 * Forward-geocoding helper backed by the Mapbox Geocoding API v5.
 *
 * Used by the address autocomplete component on the Lead Finder + Lookup
 * pages so contractors get real, normalized address suggestions instead of
 * typing free-text strings that later produce sparse-data leads.
 *
 * Design notes:
 *   - Mapbox token is a *public* (pk.*) token — safe to expose to the client.
 *   - We restrict the search to the US (`country=us`) since QuotesFlow is
 *     US-roofer-only today; can be widened later.
 *   - We limit results to address-level features (`types=address`) which
 *     filters out states/zip codes/cities — contractors need a specific
 *     property to unlock.
 *   - We cache responses for 60s, keyed on the raw query, so a user
 *     deleting and retyping the same address doesn't re-bill us.
 *   - Mapbox free tier is 100k requests/month per token; the debounce
 *     (250ms) + cache should keep us well under that even at scale.
 */

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
const ENDPOINT = 'https://api.mapbox.com/geocoding/v5/mapbox.places';
const CACHE_TTL_MS = 60_000;
const cache = new Map(); // q → { at: ms, result: [...] }

/**
 * Forward-geocode a free-text address. Returns up to 6 address suggestions.
 * Each suggestion: { id, place_name, address, center: [lng, lat],
 *                    accuracy, relevance, context }
 */
export async function searchAddresses(query, { signal } = {}) {
    const q = String(query || '').trim();
    if (q.length < 3) return [];
    if (!TOKEN) {
        console.warn('[geocode] REACT_APP_MAPBOX_TOKEN missing — autocomplete disabled.');
        return [];
    }
    // Cache hit?
    const cached = cache.get(q);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.result;

    const params = new URLSearchParams({
        access_token: TOKEN,
        country: 'us',
        types: 'address',
        limit: '6',
        autocomplete: 'true',
    });
    const url = `${ENDPOINT}/${encodeURIComponent(q)}.json?${params}`;
    try {
        const r = await fetch(url, { signal });
        if (!r.ok) {
            console.warn('[geocode] Mapbox returned', r.status);
            return [];
        }
        const data = await r.json();
        const features = (data?.features || []).map((f) => ({
            id: f.id,
            place_name: f.place_name,
            address: f.place_name,
            center: f.center,                              // [lng, lat]
            accuracy: f.properties?.accuracy || 'street',
            relevance: f.relevance,
            // Useful for downstream UI grouping (city, region, postcode)
            context: (f.context || []).reduce((acc, c) => {
                const kind = (c.id || '').split('.')[0];
                acc[kind] = c.text;
                return acc;
            }, {}),
        }));
        cache.set(q, { at: Date.now(), result: features });
        // Best-effort cache size cap (keep ~200 most-recent entries)
        if (cache.size > 220) {
            const oldest = [...cache.entries()]
                .sort((a, b) => a[1].at - b[1].at)
                .slice(0, 30)
                .map(([k]) => k);
            oldest.forEach((k) => cache.delete(k));
        }
        return features;
    } catch (e) {
        if (e.name !== 'AbortError') console.warn('[geocode] error', e);
        return [];
    }
}
